import { isOnline } from '@/utils/network';
import { loadingStore } from '@/stores/loadingStore';

class ACTApiService {
    static instance = null;

    constructor() {
        if (ACTApiService.instance) {
            return ACTApiService.instance;
        }
        ACTApiService.instance = this;
        
        this.baseUrl = process.env.NODE_ENV === 'production' 
            ? 'https://ucrbraingamecenter.org/api'
            : 'https://localhost:5001/api';
        this.actGroupId = null;
        this.authToken = null;
    }

    generateTaskId(method, endpoint) {
        return `${method}_${endpoint}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    }

    startApiLoading(message) {
        const taskId = this.generateTaskId('API', 'call');
        loadingStore.startLoading(taskId, message);
        return taskId;
    }

    stopApiLoading(taskId) {
        if (taskId) {
            loadingStore.stopLoading(taskId);
        }
    }

    setAuthToken(token) {
        this.authToken = token;
        sessionStorage.setItem('actApiAuthToken', token);
    }

    getAuthToken() {
        if (!this.authToken) {
            this.authToken = sessionStorage.getItem('actApiAuthToken');
        }
        return this.authToken;
    }

    async getTokenFromCognito() {
        if (!isOnline()) {
            console.warn('Cannot get token while offline');
            return null;
        }

        const taskId = this.startApiLoading('Getting authentication token...');

        try {
            const { CognitoUserPool } = await import('amazon-cognito-identity-js');
            
            const poolData = {
                UserPoolId: 'us-east-2_3novul0pN',
                ClientId: '51v76uda8o5l6niokhg852qnj5'
            };
            
            const userPool = new CognitoUserPool(poolData);
            const cognitoUser = userPool.getCurrentUser();
            
            if (!cognitoUser) {
                console.warn('No current user found');
                return null;
            }
            
            return new Promise((resolve, reject) => {
                cognitoUser.getSession((err, session) => {
                    if (err) {
                        console.error('Error getting session:', err);
                        reject(err);
                        return;
                    }
                    
                    if (session && session.isValid()) {
                        const token = session.getIdToken().getJwtToken();
                        this.setAuthToken(token);
                        resolve(token);
                    } else {
                        console.warn('Session is invalid or expired');
                        resolve(null);
                    }
                });
            });
        } catch (error) {
            console.error('Error getting token from Cognito:', error);
            return null;
        } finally {
            this.stopApiLoading(taskId);
        }
    }

    async fetchWithAuth(url, options = {}) {
        const token = this.getAuthToken();
        
        if (!token) {
            try {
                const newToken = await this.getTokenFromCognito();
                if (!newToken) {
                    throw new Error('Authentication token is not available. Please login first.');
                }
            } catch (error) {
                throw new Error('Failed to get authentication token: ' + error.message);
            }
        }

        if (!isOnline()) {
            throw new Error('Cannot make API requests while offline.');
        }

        const headers = {
            'Authorization': `Bearer ${this.getAuthToken()}`,
            'Content-Type': 'application/json',
            ...options.headers
        };

        return fetch(url, {
            ...options,
            headers
        });
    }

    async getLGroups() {
        const taskId = this.startApiLoading('Loading participant groups...');
        
        try {
            let response = await this.fetchWithAuth(`${this.baseUrl}/ACTInterview/lgroups`);
            
            if (!response.ok) {
                throw new Error(`Failed to fetch LGroups: ${response.status} ${response.statusText}`);
            }
            
            let data = await response.json();
            
            if (Array.isArray(data) && data.length === 0) {
                loadingStore.setGlobalLoading(true, 'Creating ACT group...');
                
                const createResponse = await this.fetchWithAuth(
                    `${this.baseUrl}/ACTInterview/lgroups`,
                    {
                        method: 'POST',
                        body: JSON.stringify({
                            lGroupName: "ACT",
                            studyAcronym: "ACTB",
                            initialVisitType: "Biennial 2025"
                        })
                    }
                );

                if (!createResponse.ok) {
                    throw new Error(`Failed to create ACT group: ${createResponse.status} ${createResponse.statusText}`);
                }

                // Make another GET request to fetch the updated groups
                response = await this.fetchWithAuth(`${this.baseUrl}/ACTInterview/lgroups`);
                
                if (!response.ok) {
                    throw new Error(`Failed to fetch LGroups after creation: ${response.status} ${response.statusText}`);
                }
                
                data = await response.json();
            }
            
            const actGroup = data.find(group => group.DisplayName === 'ACT');
            if (actGroup) {
                this.actGroupId = actGroup.LGroupId;
                localStorage.setItem('actGroupId', this.actGroupId);
                return this.actGroupId;
            } else {
                console.warn('ACT group not found in the response');
                return null;
            }
        } catch (error) {
            console.error('Error in getLGroups:', error);
            throw error;
        } finally {
            this.stopApiLoading(taskId);
        }
    }

    async getVisitTypes(lGroupId = null) {
        if (!lGroupId) {
            lGroupId = this.getStoredActGroupId();
            if (!lGroupId) {
                throw new Error('LGroup ID is not available. Please fetch LGroups first.');
            }
        }

        const taskId = this.startApiLoading('Loading visit types...');

        try {
            const response = await this.fetchWithAuth(`${this.baseUrl}/ACTInterview/lgroups/${lGroupId}/visitTypes`);
            
            if (!response.ok) {
                throw new Error(`Failed to fetch visit types: ${response.status} ${response.statusText}`);
            }
            
            const visitTypes = await response.json();
            
            const biennialVisitType = visitTypes.find(type => type.VisitTypeName === 'Biennial 2025');
            if (biennialVisitType) {
                localStorage.setItem('biennialVisitTypeId', biennialVisitType.VisitTypeId);
                return biennialVisitType.VisitTypeId;
            } else {
                console.warn('Biennial 2025 visit type not found');
                return null;
            }
        } catch (error) {
            console.error('Error fetching visit types:', error);
            throw error;
        } finally {
            this.stopApiLoading(taskId);
        }
    }

    async getParticipants(lGroupId = null) {
        if (!lGroupId) {
            lGroupId = this.getStoredActGroupId();
            if (!lGroupId) {
                throw new Error('LGroup ID is not available. Please fetch LGroups first.');
            }
        }

        const taskId = this.startApiLoading('Loading participants...');

        try {
            const response = await this.fetchWithAuth(`${this.baseUrl}/ACTInterview/lgroups/${lGroupId}/participants`);
            
            if (!response.ok) {
                throw new Error(`Failed to fetch participants: ${response.status} ${response.statusText}`);
            }
            
            const participants = await response.json();
            return participants;
        } catch (error) {
            console.error('Error fetching participants:', error);
            throw error;
        } finally {
            this.stopApiLoading(taskId);
        }
    }

    async getVisit(participantId, visitTypeId) {
        const taskId = this.startApiLoading('Loading visit data...');

        try {
            const response = await this.fetchWithAuth(`${this.baseUrl}/ACTInterview/participants/${participantId}/visits/${visitTypeId}`);
            
            if (!response.ok) {
                if (response.status === 500) {
                    const responseText = await response.text();
                    if (responseText === "Visit not found.") {
                        console.log('API returned 500 status but with expected "Visit not found" message - this is normal behavior');
                        return { notFound: true, error: responseText };
                    }
                    return { error: responseText };
                }
                throw new Error(`Failed to fetch visit: ${response.status} ${response.statusText}`);
            }
            
            const visitData = await response.json();
            return { success: true, data: visitData };
        } catch (error) {
            console.error('Error fetching visit:', error);
            return { error: error.message };
        } finally {
            this.stopApiLoading(taskId);
        }
    }

    async createVisit(lGroupId, participantId, visitTypeId, serverCode) {
        const requestBody = {
            lGroupID: lGroupId,
            participantID: participantId,
            visitTypeID: visitTypeId,
            serverCode: serverCode,
            visitData: {}
        };

        console.log('Creating visit with data:', JSON.stringify(requestBody, null, 2));

        const taskId = this.startApiLoading('Creating visit...');

        try {
            const response = await this.fetchWithAuth(
                `${this.baseUrl}/ACTInterview/visits`, 
                {
                    method: 'POST',
                    body: JSON.stringify(requestBody)
                }
            );
            
            if (!response.ok) {
                const responseText = await response.text();
                
                // Special case: API returns 500 with "Visit saved successfully"
                if (response.status === 500 && responseText === "Visit saved successfully.") {
                    console.log('⚠️ API returned 500 status but with "Visit saved successfully" message - this appears to be normal API behavior');
                    // Not throwing error, just returning a success indicator
                    return { success: true, note: 'Visit created despite error status' };
                }
                
                console.warn(`Server returned ${response.status} error: ${responseText}`);
                throw new Error(`Server returned ${response.status}: ${responseText}`);
            }
            
            const visitData = await response.json();
            return visitData;
        } catch (error) {
            console.error('Error in createVisit request:', error);
            throw error;
        } finally {
            this.stopApiLoading(taskId);
        }
    }

    async updateVisitData(lGroupId, participantId, visitTypeId, serverCode) {
        await new Promise(resolve => setTimeout(resolve, 100));
        
        const participantDisplayId = serverCode;
        const subjectData = JSON.parse(localStorage.getItem('subjectData') || '{}');
        const participantData = subjectData[participantDisplayId] || {};
        
        const cleanedData = { ...participantData };
        
        // Remove duplicate verbal fluency data
        if (cleanedData.verbalFluencyLetterFData && cleanedData.letterFData) {
            delete cleanedData.letterFData;
            console.log('Removed duplicate letterFData from visit data');
        }
        
        if (cleanedData.verbalFluencySupermarketData && cleanedData.supermarketData) {
            delete cleanedData.supermarketData;
            console.log('Removed duplicate supermarketData from visit data');
        }
        
        console.log(`Updating visit data for participant ${participantDisplayId}`, cleanedData);
        
        const requestBody = {
            lGroupID: lGroupId,
            participantID: participantId,
            visitTypeID: visitTypeId,
            serverCode: serverCode,
            visitData: cleanedData
        };
        
        const taskId = this.startApiLoading('Saving visit data...');
        
        try {
            const response = await this.fetchWithAuth(
                `${this.baseUrl}/ACTInterview/visits`, 
                {
                    method: 'POST',
                    body: JSON.stringify(requestBody)
                }
            );
            
            if (!response.ok) {
                const responseText = await response.text();
                console.warn(`Failed to update visit data. Server returned ${response.status}: ${responseText}`);
                throw new Error(`Failed to update visit data: ${response.status} ${responseText}`);
            }
            
            console.log('Visit data updated successfully');
            return await response.json();
        } catch (error) {
            console.error('Error updating visit data:', error);
            throw error;
        } finally {
            this.stopApiLoading(taskId);
        }
    }

    getStoredActGroupId() {
        return this.actGroupId || localStorage.getItem('actGroupId');
    }

    async getVisitLogs(visitId) {
        const taskId = this.startApiLoading('Loading visit logs...');

        try {
            const response = await this.fetchWithAuth(`${this.baseUrl}/ACTInterview/visits/${visitId}/logs`);
            
            if (!response.ok) {
                throw new Error(`Failed to fetch visit logs: ${response.status} ${response.statusText}`);
            }
            
            const logs = await response.json();
            console.log('Visit logs retrieved:', logs);
            return logs;
        } catch (error) {
            console.error('Error fetching visit logs:', error);
            throw error;
        } finally {
            this.stopApiLoading(taskId);
        }
    }

    async getLogBinary(logId) {
        const taskId = this.startApiLoading('Loading log file...');

        try {
            const response = await this.fetchWithAuth(`${this.baseUrl}/ACTInterview/logs/${logId}/binary`);
            
            if (!response.ok) {
                throw new Error(`Failed to fetch log binary: ${response.status}`);
            }
            
            return await response.blob();
        } catch (error) {
            console.error('Error fetching log binary:', error);
            throw error;
        } finally {
            this.stopApiLoading(taskId);
        }
    }

    async getLogTranscript(logId) {
        const taskId = this.startApiLoading('Loading transcript...');

        try {
            const response = await this.fetchWithAuth(`${this.baseUrl}/ACTInterview/logs/${logId}/transcript`);
            
            if (!response.ok) {
                throw new Error(`Failed to fetch transcript: ${response.status}`);
            }
            
            return await response.json();
        } catch (error) {
            console.error('Error fetching transcript:', error);
            throw error;
        } finally {
            this.stopApiLoading(taskId);
        }
    }
}

export default ACTApiService; 